import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as React from "react";
import { LightboxDefaultProps } from "../../types.js";
import { cleanup, clsx, cssClass, cssVar, makeUseContext } from "../utils.js";
import { createModule } from "../config.js";
import { useContainerRect, useLatest, useLayoutEffect, useRTL, useSensors } from "../hooks/index.js";
import { useEvents, useTimeouts } from "../contexts/index.js";
var SWIPE_OFFSET_THRESHOLD = 30;
var ControllerContext = React.createContext(null);
export var useController = makeUseContext("useController", "ControllerContext", ControllerContext);
export var Controller = function Controller(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useContainerRect = useContainerRect(),
      containerRef = _useContainerRect.containerRef,
      setContainerRef = _useContainerRect.setContainerRef,
      containerRect = _useContainerRect.containerRect;

  var _useSensors = useSensors(),
      registerSensors = _useSensors.registerSensors,
      subscribeSensors = _useSensors.subscribeSensors;

  var _useEvents = useEvents(),
      subscribe = _useEvents.subscribe,
      publish = _useEvents.publish;

  var _useTimeouts = useTimeouts(),
      setTimeout = _useTimeouts.setTimeout,
      clearTimeout = _useTimeouts.clearTimeout;

  var isRTL = useLatest(useRTL());

  var _React$useState = React.useState({
    currentIndex: props.index,
    globalIndex: props.index
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      state = _React$useState2[0],
      setState = _React$useState2[1];

  var latestProps = useLatest(props);
  var refs = React.useRef({
    state: state,
    props: props,
    swipeOffset: 0,
    swipeIntent: 0,
    swipeAnimationDuration: props.animation.swipe,
    wheelResidualMomentum: 0,
    pointers: []
  });
  refs.current.state = state;
  refs.current.props = props;
  useLayoutEffect(function () {
    var preventDefault = function preventDefault(event) {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.ctrlKey) {
        event.preventDefault();
      }
    };

    var node = containerRef.current;

    if (node) {
      node.addEventListener("wheel", preventDefault, {
        passive: false
      });
    }

    return function () {
      if (node) {
        node.removeEventListener("wheel", preventDefault);
      }
    };
  }, [containerRef]);
  React.useEffect(function () {
    var _a;

    if (refs.current.props.controller.focus) {
      (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, [containerRef, refs]);
  React.useEffect(function () {
    var _a, _b;

    (_b = (_a = refs.current.props.on).view) === null || _b === void 0 ? void 0 : _b.call(_a, state.currentIndex);
  }, [state.currentIndex]);
  var updateSwipeOffset = React.useCallback(function () {
    var _a, _b;

    var offsetVar = cssVar("swipe_offset");

    if (refs.current.swipeOffset !== 0) {
      (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(offsetVar, "".concat(Math.round(refs.current.swipeOffset), "px"));
    } else {
      (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.style.removeProperty(offsetVar);
    }
  }, [containerRef]);
  useLayoutEffect(function () {
    updateSwipeOffset();
  });
  var rerender = React.useCallback(function () {
    setState(function (prev) {
      return _objectSpread({}, prev);
    });
  }, []);
  var resetSwipe = React.useCallback(function () {
    var current = refs.current;
    current.swipeOffset = 0;
    current.swipeIntent = 0;
    current.swipeStartTime = undefined;
    clearTimeout(current.swipeResetCleanup);
    current.swipeResetCleanup = undefined;
    clearTimeout(current.swipeIntentCleanup);
    current.swipeIntentCleanup = undefined;
  }, [clearTimeout]);
  var rtl = React.useCallback(function (value) {
    return (isRTL.current ? -1 : 1) * (typeof value === "number" ? value : 1);
  }, [isRTL]);
  var isSwipeValid = React.useCallback(function (offset) {
    var _refs$current = refs.current,
        currentIndex = _refs$current.state.currentIndex,
        _refs$current$props = _refs$current.props,
        carousel = _refs$current$props.carousel,
        slides = _refs$current$props.slides;
    return !(carousel.finite && (rtl(offset) > 0 && currentIndex === 0 || rtl(offset) < 0 && currentIndex === slides.length - 1));
  }, [rtl]);
  var swipe = React.useCallback(function (direction) {
    var count = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

    var _a;

    var current = refs.current;
    var slidesCount = current.props.slides.length;
    var swipeAnimationDuration = current.props.animation.swipe;
    var _current$state = current.state,
        currentIndex = _current$state.currentIndex,
        globalIndex = _current$state.globalIndex;
    var swipeOffset = current.swipeOffset;
    var newSwipeState = "swipe-animation";
    var newSwipeAnimationDuration = swipeAnimationDuration * count;

    if (!direction) {
      var containerWidth = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth;
      var elapsedTime = current.swipeStartTime ? Date.now() - current.swipeStartTime : 0;
      var expectedTime = containerWidth ? swipeAnimationDuration / containerWidth * Math.abs(swipeOffset) : swipeAnimationDuration;

      if (containerWidth && (swipeOffset !== 0 && elapsedTime < swipeAnimationDuration || Math.abs(swipeOffset) > 0.5 * containerWidth)) {
        newSwipeAnimationDuration = swipeAnimationDuration / containerWidth * (containerWidth - Math.abs(swipeOffset));

        if (elapsedTime < expectedTime) {
          newSwipeAnimationDuration = newSwipeAnimationDuration / expectedTime * Math.max(elapsedTime, expectedTime / 5);
        }

        direction = rtl(swipeOffset) > 0 ? "prev" : "next";
      } else {
        newSwipeAnimationDuration = swipeAnimationDuration / 2;
      }
    }

    var newState = {};

    if (direction === "prev") {
      if (isSwipeValid(rtl(1))) {
        newState.currentIndex = (currentIndex - count + slidesCount) % slidesCount;
        newState.globalIndex = globalIndex - count;
      } else {
        newSwipeState = undefined;
        newSwipeAnimationDuration = swipeAnimationDuration;
      }
    } else if (direction === "next") {
      if (isSwipeValid(rtl(-1))) {
        newState.currentIndex = (currentIndex + count) % slidesCount;
        newState.globalIndex = globalIndex + count;
      } else {
        newSwipeState = undefined;
        newSwipeAnimationDuration = swipeAnimationDuration;
      }
    }

    newSwipeAnimationDuration = Math.round(newSwipeAnimationDuration);
    resetSwipe();
    current.swipeState = newSwipeState;
    current.swipeAnimationDuration = newSwipeAnimationDuration;

    if (newSwipeState) {
      setTimeout(function () {
        current.swipeState = undefined;
        current.swipeAnimationDuration = current.props.animation.swipe;
        rerender();
      }, newSwipeAnimationDuration);
    }

    publish("controller-swipe", _objectSpread(_objectSpread({}, newState), {}, {
      animationDuration: current.swipeAnimationDuration
    }));
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), newState);
    });
  }, [setTimeout, resetSwipe, isSwipeValid, rerender, containerRef, rtl, publish]);
  React.useEffect(function () {
    return cleanup(subscribe("prev", function (_, count) {
      return swipe("prev", typeof count === "number" ? count : undefined);
    }), subscribe("next", function (_, count) {
      return swipe("next", typeof count === "number" ? count : undefined);
    }));
  }, [subscribe, swipe]);
  React.useEffect(function () {
    return subscribeSensors("onKeyUp", function (event) {
      if (event.code === "Escape") {
        publish("close");
      }
    });
  }, [subscribeSensors, publish]);
  var clearPointer = React.useCallback(function (event) {
    var _current$pointers;

    var current = refs.current;

    if (current.activePointer === event.pointerId) {
      current.activePointer = undefined;
    }

    (_current$pointers = current.pointers).splice.apply(_current$pointers, [0, current.pointers.length].concat(_toConsumableArray(current.pointers.filter(function (p) {
      return p.pointerId !== event.pointerId;
    }))));
  }, []);
  var addPointer = React.useCallback(function (event) {
    clearPointer(event);
    refs.current.pointers.push(event);
  }, [clearPointer]);
  var onPointerDown = React.useCallback(function (event) {
    addPointer(event);
  }, [addPointer]);
  var onPointerMove = React.useCallback(function (event) {
    var current = refs.current;
    var original = current.pointers.find(function (p) {
      return p.pointerId === event.pointerId;
    });

    if (original) {
      var deltaX = event.clientX - original.clientX;
      var deltaY = event.clientY - original.clientY;

      if (!current.swipeState) {
        if (isSwipeValid(deltaX) && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_OFFSET_THRESHOLD) {
          addPointer(event);
          current.activePointer = event.pointerId;
          current.swipeStartTime = Date.now();
          current.swipeState = "swipe";
          rerender();
        }
      } else if (current.swipeState === "swipe") {
        if (event.pointerId === current.activePointer) {
          current.swipeOffset = deltaX;
          updateSwipeOffset();
        }
      }
    }
  }, [addPointer, updateSwipeOffset, isSwipeValid, rerender]);
  var onPointerUp = React.useCallback(function (event) {
    var current = refs.current;

    if (current.pointers.find(function (p) {
      return p.pointerId === event.pointerId;
    }) && current.swipeState === "swipe" && current.activePointer === event.pointerId) {
      swipe();
    }

    clearPointer(event);
  }, [clearPointer, swipe]);
  React.useEffect(function () {
    return cleanup(subscribeSensors("onPointerDown", onPointerDown), subscribeSensors("onPointerMove", onPointerMove), subscribeSensors("onPointerUp", onPointerUp), subscribeSensors("onPointerLeave", onPointerUp), subscribeSensors("onPointerCancel", onPointerUp));
  }, [subscribeSensors, onPointerDown, onPointerMove, onPointerUp]);
  var onWheel = React.useCallback(function (event) {
    var _a;

    if (event.ctrlKey) {
      return;
    }

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      return;
    }

    var current = refs.current;

    if (!current.swipeState) {
      if (Math.abs(event.deltaX) <= 1.2 * Math.abs(current.wheelResidualMomentum)) {
        current.wheelResidualMomentum = event.deltaX;
        return;
      }

      if (!isSwipeValid(-event.deltaX)) {
        return;
      }

      current.swipeIntent += event.deltaX;
      clearTimeout(current.swipeIntentCleanup);

      if (Math.abs(current.swipeIntent) > SWIPE_OFFSET_THRESHOLD) {
        current.swipeStartTime = Date.now();
        current.swipeIntent = 0;
        current.wheelResidualMomentum = 0;
        current.swipeState = "swipe";
        rerender();
      } else {
        current.swipeIntentCleanup = setTimeout(function () {
          current.swipeIntent = 0;
          current.swipeIntentCleanup = undefined;
        }, current.props.animation.swipe);
      }
    } else if (current.swipeState === "swipe") {
      var containerWidth = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth;

      if (containerWidth) {
        current.swipeOffset -= event.deltaX;
        current.swipeOffset = Math.min(Math.abs(current.swipeOffset), containerWidth) * Math.sign(current.swipeOffset);
        updateSwipeOffset();
        clearTimeout(current.swipeResetCleanup);

        if (Math.abs(current.swipeOffset) > 0.2 * containerWidth) {
          current.wheelResidualMomentum = event.deltaX;
          swipe();
          return;
        }

        var currentSwipeOffset = current.swipeOffset;
        current.swipeResetCleanup = setTimeout(function () {
          current.swipeResetCleanup = undefined;

          if (current.swipeState === "swipe" && current.swipeOffset === currentSwipeOffset) {
            resetSwipe();
            current.swipeState = undefined;
            rerender();
          }
        }, 2 * current.props.animation.swipe);
      }
    } else {
      current.wheelResidualMomentum = event.deltaX;
    }
  }, [updateSwipeOffset, setTimeout, clearTimeout, swipe, resetSwipe, rerender, isSwipeValid, containerRef]);
  React.useEffect(function () {
    return subscribeSensors("onWheel", onWheel);
  }, [subscribeSensors, onWheel]);
  var transferFocus = React.useCallback(function () {
    var _a;

    return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, [containerRef]);
  var context = React.useMemo(function () {
    return {
      latestProps: latestProps,
      currentIndex: state.currentIndex,
      globalIndex: state.globalIndex,
      subscribeSensors: subscribeSensors,
      transferFocus: transferFocus,
      containerRect: containerRect,
      containerRef: containerRef
    };
  }, [latestProps, state.currentIndex, state.globalIndex, subscribeSensors, transferFocus, containerRect, containerRef]);
  return React.createElement("div", _objectSpread(_objectSpread({
    ref: setContainerRef,
    className: clsx(cssClass("container"), cssClass("fullsize"), refs.current.swipeState === "swipe" && cssClass("container_swipe")),
    style: _objectSpread(_objectSpread({}, refs.current.swipeAnimationDuration !== LightboxDefaultProps.animation.swipe ? _defineProperty({}, cssVar("swipe_animation_duration"), "".concat(Math.round(refs.current.swipeAnimationDuration), "ms")) : null), props.controller.touchAction !== "none" ? _defineProperty({}, cssVar("controller_touch_action"), props.controller.touchAction) : null)
  }, props.controller.aria ? {
    role: "presentation",
    "aria-live": "polite"
  } : null), {}, {
    tabIndex: -1
  }, registerSensors), containerRect && React.createElement(ControllerContext.Provider, {
    value: context
  }, children));
};
export var ControllerModule = createModule("controller", Controller);